/**
 * Documenti: fatture, preventivi, ordini e ricevute estratti automaticamente
 * dalle email (numero, importo, scadenza, controparte). "Cerca documenti" avvia
 * la scansione AI delle email recenti.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { FileText, Sparkles, Loader2, Trash2, Mail, CalendarClock } from 'lucide-react';
import { format, parseISO, isBefore, addDays } from 'date-fns';
import { it } from 'date-fns/locale';
import { toast } from 'sonner';
import { InboxSidebar } from '@/components/layout/InboxSidebar';
import { Button } from '@/components/ui/button';
import { documentsApi, type ExtractedDocument } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';

const TYPES: { key: string; label: string }[] = [
  { key: '', label: 'Tutti' },
  { key: 'fattura', label: 'Fatture' },
  { key: 'preventivo', label: 'Preventivi' },
  { key: 'ordine', label: 'Ordini' },
  { key: 'ricevuta', label: 'Ricevute' },
];
const TYPE_COLOR: Record<string, string> = {
  fattura: 'bg-blue-100 text-blue-700',
  preventivo: 'bg-violet-100 text-violet-700',
  ordine: 'bg-amber-100 text-amber-700',
  ricevuta: 'bg-emerald-100 text-emerald-700',
  altro: 'bg-muted text-muted-foreground',
};

function fmtAmount(amount: string | null, currency: string | null): string | null {
  if (!amount) return null;
  const n = Number(amount);
  if (!isFinite(n)) return null;
  try {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: currency || 'EUR' }).format(n);
  } catch {
    return `${n.toFixed(2)} ${currency || ''}`.trim();
  }
}

export function DocumentsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [type, setType] = useState('');

  const q = useQuery({ queryKey: ['documents', type], queryFn: () => documentsApi.list(type || undefined) });
  const docs = q.data?.documents ?? [];

  const scanMut = useMutation({
    mutationFn: () => documentsApi.scan(),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['documents'] });
      toast.success(r.found > 0 ? `${r.found} documenti trovati` : 'Nessun nuovo documento nelle email recenti');
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Scansione fallita'),
  });
  const delMut = useMutation({
    mutationFn: (id: string) => documentsApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }),
  });

  const soon = (d: string | null) => d && isBefore(parseISO(d), addDays(new Date(), 7));

  return (
    <div className="h-screen flex bg-background">
      <InboxSidebar />
      <main className="flex-1 flex flex-col min-w-0">
        <div className="border-b px-6 py-4 flex items-center gap-3 flex-wrap">
          <h1 className="text-xl font-bold mr-2 flex items-center gap-2"><FileText className="h-5 w-5 text-primary" /> Documenti</h1>
          <div className="flex items-center gap-1">
            {TYPES.map((t) => (
              <Button key={t.key} variant={type === t.key ? 'default' : 'outline'} size="sm" onClick={() => setType(t.key)}>
                {t.label}
              </Button>
            ))}
          </div>
          <Button className="ml-auto" size="sm" onClick={() => scanMut.mutate()} disabled={scanMut.isPending}>
            {scanMut.isPending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
            Cerca documenti
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {q.isLoading ? (
            <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : docs.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground">
              <FileText className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <div className="text-sm">Nessun documento.</div>
              <div className="text-xs mt-1">Premi “Cerca documenti” per estrarre fatture, preventivi e ordini dalle email.</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 max-w-5xl">
              {docs.map((d: ExtractedDocument) => {
                const amount = fmtAmount(d.amount, d.currency);
                return (
                  <div key={d.id} className="group rounded-lg border p-4 hover:border-primary/40 transition-colors">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full capitalize ${TYPE_COLOR[d.doc_type] || TYPE_COLOR.altro}`}>{d.doc_type}</span>
                        {d.doc_number && <span className="text-xs text-muted-foreground">n. {d.doc_number}</span>}
                      </div>
                      {amount && <span className="text-base font-bold">{amount}</span>}
                    </div>
                    <div className="mt-1.5 text-sm font-medium truncate">{d.counterpart || d.subject || '(senza dettagli)'}</div>
                    {d.counterpart && d.subject && <div className="text-xs text-muted-foreground truncate">{d.subject}</div>}
                    <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                      {d.doc_date && <span>Data: {format(parseISO(d.doc_date), 'd MMM yyyy', { locale: it })}</span>}
                      {d.due_date && (
                        <span className={`inline-flex items-center gap-1 ${soon(d.due_date) ? 'text-amber-600 font-medium' : ''}`}>
                          <CalendarClock className="h-3 w-3" /> Scadenza: {format(parseISO(d.due_date), 'd MMM yyyy', { locale: it })}
                        </span>
                      )}
                    </div>
                    <div className="mt-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => navigate(`/?message=${d.message_id}`)}>
                        <Mail className="h-3.5 w-3.5 mr-1" /> Apri email
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-destructive ml-auto" onClick={() => delMut.mutate(d.id)}>
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Rimuovi
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
